// src/services/attendanceService.js
import { apiRequest } from './api';
import { formatAttendanceTime } from '../utils/dateUtils';

export const attendanceService = {
    // Get attendance records with filters
    getAttendance: async (params = {}) => {
        try {
            const response = await apiRequest.get('/attendance', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch attendance records');
        }
    },

    // Mark attendance (for mobile/web)
    markAttendance: async (attendanceData) => {
        try {
            const response = await apiRequest.post('/attendance/mark', attendanceData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to mark attendance');
        }
    },

    // Get attendance statistics
    getAttendanceStats: async (params = {}) => {
        try {
            const response = await apiRequest.get('/attendance/stats', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch attendance statistics');
        }
    },

    // Manual attendance entry (admin only)
    createManualAttendance: async (attendanceData) => {
        try {
            const response = await apiRequest.post('/attendance', attendanceData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to create manual attendance entry');
        }
    },

    // Update attendance record
    updateAttendance: async (attendanceId, attendanceData) => {
        try {
            const response = await apiRequest.put(`/attendance/${attendanceId}`, attendanceData);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to update attendance record');
        }
    },

    // Review attendance record (admin)
    reviewAttendance: async (attendanceId, payload = {}) => {
        try {
            const response = await apiRequest.post('/attendance/review', {
                attendance_id: attendanceId,
                ...payload
            });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to review attendance record');
        }
    },

    // Delete attendance record
    deleteAttendance: async (attendanceId) => {
        try {
            const response = await apiRequest.delete(`/attendance/${attendanceId}`);
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to delete attendance record');
        }
    },

    // Get daily attendance summary
    getDailyAttendance: async (date, premisesId = null) => {
        try {
            const params = { date };
            if (premisesId) params.premises_id = premisesId;

            const response = await apiRequest.get('/attendance/daily', { params });
            return response.data.data;
        } catch (error) {
            throw new Error(error.message || 'Failed to fetch daily attendance');
        }
    },

    // Export attendance data: raw log (csv/pdf) or a per-staff summary (summary/summary_pdf)
    exportAttendance: async (format = 'csv', filters = {}) => {
        try {
            if (format === 'csv' || format === 'summary') {
                const response = await apiRequest.get('/attendance', {
                    params: { ...filters, export: format },
                    responseType: 'blob'
                });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const namePrefix = format === 'summary' ? 'attendance_summary' : 'attendance';
                link.setAttribute('download', `${namePrefix}_${new Date().toISOString().split('T')[0]}.csv`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                return true;
            }

            if (format !== 'pdf' && format !== 'summary_pdf') {
                throw new Error('Invalid export format. Use csv, summary, pdf, or summary_pdf');
            }

            const response = await apiRequest.get('/attendance', {
                params: { ...filters, export: format }
            });
            const payload = response?.data?.data || response?.data || {};
            const meta = payload.meta || {};

            const user = JSON.parse(localStorage.getItem('haazri_user') || '{}');
            const userType = String(user?.user_type || '').toLowerCase();
            const orgId = user?.org_id;
            const appLogo = localStorage.getItem('app_logo') || '';
            const orgLogo = orgId ? (localStorage.getItem(`org_logo_${orgId}`) || '') : '';
            const logoUrl = (userType === 'client_admin' || userType === 'app_admin') && orgLogo ? orgLogo : appLogo;

            const esc = (val) => String(val ?? '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
            const fmtDate = (dateStr) => {
                if (!dateStr) return '-';
                const d = new Date(dateStr);
                return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
            };

            let reportTitle;
            let summaryCards;
            let tableHead;
            let tableBody;

            if (format === 'summary_pdf') {
                const rows = Array.isArray(payload.summary) ? payload.summary : [];
                reportTitle = 'Attendance Summary Report';
                summaryCards = `
                    <div class="card"><div class="label">From Date</div><div class="value">${esc(fmtDate(meta.date_from))}</div></div>
                    <div class="card"><div class="label">To Date</div><div class="value">${esc(fmtDate(meta.date_to))}</div></div>
                    <div class="card"><div class="label">Staff</div><div class="value">${esc(meta.total_staff ?? rows.length)}</div></div>
                `;
                tableHead = `
                    <th>#</th><th>Staff Name</th><th>Staff ID</th><th>Premises</th><th>Days Present</th><th>Days Absent</th><th>Days Late</th><th>Days On Leave</th><th>Days Holiday</th><th>Total Hours</th><th>Attendance %</th>
                `;
                tableBody = rows.map((row, idx) => `
                    <tr>
                        <td>${idx + 1}</td>
                        <td>${esc(row['Staff Name'] || '-')}</td>
                        <td>${esc(row['Staff ID'] || '-')}</td>
                        <td>${esc(row['Premises'] || '-')}</td>
                        <td>${esc(row['Days Present'])}</td>
                        <td>${esc(row['Days Absent'])}</td>
                        <td>${esc(row['Days Late'])}</td>
                        <td>${esc(row['Days On Leave'])}</td>
                        <td>${esc(row['Days Holiday'])}</td>
                        <td>${esc(row['Total Hours Worked'])}</td>
                        <td>${esc(row['Attendance %'])}%</td>
                    </tr>
                `).join('') || '<tr><td colspan="11" style="text-align:center; padding:16px;">No attendance records found</td></tr>';
            } else {
                const rows = Array.isArray(payload.attendance) ? payload.attendance : [];
                const normalizeStatus = (row) => {
                    const status = String(row?.status || '').toLowerCase();
                    const actualStatus = String(row?.actual_status || '').toLowerCase();
                    const hasSignIn = !!row?.sign_in_time;
                    const hasSignOut = !!row?.sign_out_time;
                    if (status === 'holiday' || actualStatus === 'holiday') return 'Holiday';
                    if (status === 'on_leave') return 'On Leave';
                    if (actualStatus === 'late' || status === 'late') return 'Late';
                    if (status === 'absent' || (!hasSignIn && !hasSignOut)) return 'Absent';
                    return 'Present';
                };

                reportTitle = 'Attendance Report';
                summaryCards = `
                    <div class="card"><div class="label">From Date</div><div class="value">${esc(fmtDate(meta.date_from))}</div></div>
                    <div class="card"><div class="label">To Date</div><div class="value">${esc(fmtDate(meta.date_to))}</div></div>
                    <div class="card"><div class="label">Records</div><div class="value">${esc(meta.total_records ?? rows.length)}</div></div>
                `;
                tableHead = `
                    <th>#</th><th>Staff Name</th><th>Staff ID</th><th>Date</th><th>Premises</th><th>Type</th><th>Shift</th><th>Sign In</th><th>Sign Out</th><th>Hours</th><th>Status</th>
                `;
                tableBody = rows.map((row, idx) => `
                    <tr>
                        <td>${idx + 1}</td>
                        <td>${esc(row.staff_name || row.username || '-')}</td>
                        <td>${esc(row.staff_id || '-')}</td>
                        <td>${esc(fmtDate(row.date))}</td>
                        <td>${esc(row.premises_name || '-')}</td>
                        <td>${esc(row.attendance_type || 'in_premises')}</td>
                        <td>${esc(row.shift_name || '-')}</td>
                        <td>${esc(formatAttendanceTime(row.sign_in_time))}</td>
                        <td>${esc(formatAttendanceTime(row.sign_out_time))}</td>
                        <td>${esc(row.working_hours || '-')}</td>
                        <td>${esc(normalizeStatus(row))}</td>
                    </tr>
                `).join('') || '<tr><td colspan="11" style="text-align:center; padding:16px;">No attendance records found</td></tr>';
            }

            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                throw new Error('Please allow popups to export PDF');
            }

            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>${esc(reportTitle)}</title>
                    <style>
                        @page { size: A4 landscape; margin: 10mm; }
                        body { font-family: Arial, sans-serif; color: #1f2937; margin: 0; padding: 0; }
                        .wrap { padding: 14px; }
                        .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
                        .brand { display: flex; align-items: center; gap: 10px; }
                        .brand img { height: 44px; max-width: 180px; object-fit: contain; }
                        .brand .fallback { font-size: 22px; font-weight: 800; color: #0f172a; }
                        .meta h1 { margin: 0; font-size: 22px; color: #0f172a; }
                        .meta p { margin: 3px 0 0; font-size: 12px; color: #64748b; text-align: right; }
                        .bar { border-top: 2px solid #e2e8f0; margin: 8px 0 12px; }
                        .summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin-bottom: 10px; }
                        .card { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px; background: #f8fafc; }
                        .card .label { font-size: 10px; text-transform: uppercase; color: #64748b; font-weight: 700; }
                        .card .value { font-size: 16px; font-weight: 800; color: #0f172a; margin-top: 2px; }
                        table { width: 100%; border-collapse: collapse; table-layout: fixed; }
                        thead { display: table-header-group; }
                        tr { page-break-inside: avoid; }
                        th, td { border: 1px solid #e5e7eb; padding: 6px; font-size: 11px; text-align: left; word-wrap: break-word; }
                        th { background: #f1f5f9; font-weight: 700; color: #1e293b; }
                    </style>
                </head>
                <body>
                    <div class="wrap">
                        <div class="header">
                            <div class="brand">
                                ${logoUrl ? `<img src="${esc(logoUrl)}" alt="Logo" />` : `<div class="fallback">eHAAZRI</div>`}
                                <div>
                                    <div style="font-size:13px;color:#334155;font-weight:700;">Attendance Management System</div>
                                </div>
                            </div>
                            <div class="meta">
                                <h1>${esc(reportTitle)}</h1>
                                <p>Generated: ${new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                            </div>
                        </div>
                        <div class="bar"></div>
                        <div class="summary">
                            ${summaryCards}
                        </div>
                        <table>
                            <thead>
                                <tr>
                                    ${tableHead}
                                </tr>
                            </thead>
                            <tbody>
                                ${tableBody}
                            </tbody>
                        </table>
                    </div>
                </body>
                </html>
            `);
            printWindow.document.close();
            printWindow.focus();
            printWindow.print();
            printWindow.close();
            return true;
        } catch (error) {
            throw new Error(error.message || 'Failed to export attendance data');
        }
    }
};
